import React from 'react';
import { Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

function OverviewPanel({ title, subtitle, badge, children }) {
    return (
        <Paper component="section" variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', boxShadow: 'none' }}>
            <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                        <Typography component="h2" sx={{ fontSize: 15, lineHeight: 1.25, fontWeight: 850 }}>
                            {title}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.2, color: 'text.secondary' }}>
                            {subtitle}
                        </Typography>
                    </Box>
                    {badge ? <Chip size="small" label={badge} sx={{ height: 24, flexShrink: 0, fontSize: 11, fontWeight: 750 }} /> : null}
                </Stack>
            </Box>
            {children}
        </Paper>
    );
}

function ProjectRecordRows({ rows }) {
    return (
        <Box>
            {rows.map((row, index) => (
                <Box
                    key={row.key}
                    component={row.onClick ? 'button' : 'div'}
                    type={row.onClick ? 'button' : undefined}
                    onClick={row.onClick}
                    sx={{
                        appearance: 'none',
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '130px minmax(0, 1fr) auto' },
                        gap: { xs: 0.4, sm: 2 },
                        alignItems: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: 1.45,
                        textAlign: 'left',
                        color: 'inherit',
                        background: 'transparent',
                        border: 0,
                        borderTop: index ? '1px solid' : 'none',
                        borderColor: 'divider',
                        cursor: row.onClick ? 'pointer' : 'default',
                        '&:hover': row.onClick ? { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.045) } : undefined
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                        {row.label}
                    </Typography>
                    <Typography sx={{ minWidth: 0, fontSize: 13, fontWeight: 800, overflowWrap: 'anywhere' }}>
                        {row.value || 'Not recorded'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: { sm: 'nowrap' } }}>
                        {row.source || ''}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function ReportingPeriodPanel({ reporting, periods, onSelectPeriod, onGenerate, onOpenReport, generating, openingReport }) {
    return (
        <OverviewPanel title="Current Reporting Period" subtitle="Status and evidence for the active reporting cycle." badge="Period scope">
            <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'grid', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 0.5, p: 0.4, borderRadius: 1, backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.045), overflowX: 'auto' }}>
                    {periods.map((period) => (
                        <Button
                            key={period.key}
                            size="small"
                            onClick={() => onSelectPeriod(period)}
                            sx={{
                                minWidth: 96,
                                flex: periods.length <= 3 ? 1 : '0 0 auto',
                                borderRadius: 0.75,
                                color: period.active ? 'text.primary' : 'text.secondary',
                                backgroundColor: period.active ? 'background.paper' : 'transparent',
                                boxShadow: period.active ? '0 1px 4px rgba(15, 23, 42, 0.08)' : 'none'
                            }}
                        >
                            {period.label}
                        </Button>
                    ))}
                </Box>

                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box>
                        <Typography sx={{ fontSize: 30, lineHeight: 1, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                            {reporting.coverage}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>information coverage</Typography>
                    </Box>
                    <Chip size="small" label={`${reporting.gapCount} gap${reporting.gapCount === 1 ? '' : 's'}`} color={reporting.gapCount ? 'warning' : 'success'} />
                </Stack>
                <LinearProgress variant="determinate" value={reporting.coverage} color={reporting.gapCount ? 'warning' : 'success'} sx={{ height: 7, borderRadius: 1 }} />

                <Box>
                    {reporting.rows.map((row, index) => (
                        <Stack key={row.label} direction="row" spacing={1} justifyContent="space-between" sx={{ py: 1, borderTop: index ? '1px solid' : 'none', borderColor: 'divider' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{row.label}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>{row.value}</Typography>
                        </Stack>
                    ))}
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button fullWidth variant="contained" onClick={onGenerate} disabled={!reporting.canGenerate || generating}>
                        {generating ? 'Generating...' : reporting.generateLabel}
                    </Button>
                    <Button fullWidth variant="outlined" onClick={onOpenReport} disabled={!reporting.canOpen || openingReport}>
                        {openingReport ? 'Opening...' : reporting.openLabel}
                    </Button>
                </Stack>
            </Box>
        </OverviewPanel>
    );
}

function AttentionPanel({ items }) {
    return (
        <OverviewPanel
            title="Information Requiring Attention"
            subtitle="Gaps and existing findings requiring verification appear here."
            badge={`${items.length} item${items.length === 1 ? '' : 's'}`}
        >
            {items.length ? items.map((item, index) => (
                <Box
                    key={`${item.kind}-${item.title}-${index}`}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', md: 'auto 160px minmax(0, 1fr) auto' },
                        gap: { xs: 1, md: 2 },
                        alignItems: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: 1.25,
                        borderTop: index ? '1px solid' : 'none',
                        borderColor: 'divider'
                    }}
                >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.kind === 'finding' ? 'success.main' : 'warning.main' }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 850 }}>{item.title}</Typography>
                    <Typography variant="body2" sx={{ gridColumn: { xs: '2', md: 'auto' }, color: 'text.secondary' }}>{item.detail}</Typography>
                    {item.onClick ? (
                        <Button size="small" variant="text" onClick={item.onClick} sx={{ gridColumn: { xs: '2', md: 'auto' }, justifySelf: { xs: 'start', md: 'end' } }}>
                            {item.actionLabel}
                        </Button>
                    ) : null}
                </Box>
            )) : (
                <Typography variant="body2" sx={{ px: 2, py: 2.5, color: 'text.secondary' }}>
                    No current gaps or findings require attention.
                </Typography>
            )}
        </OverviewPanel>
    );
}

export default function ProjectPortalOverview(props) {
    return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.8fr) minmax(320px, 0.9fr)' }, gap: 1.5, alignItems: 'stretch' }}>
                <OverviewPanel title="Current Project Record" subtitle="Latest confirmed position, independent of any reporting period." badge="Project scope">
                    <ProjectRecordRows rows={props.projectRows} />
                </OverviewPanel>
                <ReportingPeriodPanel {...props} />
            </Box>
            <AttentionPanel items={props.attentionItems} />
        </Box>
    );
}